/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';

import { CustomerType } from '@/entities/customer-type';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import { Optimistic } from '@/utils/react-query/optimistic';

import {
  type BodyOf,
  type GetCardDetailViewOutputRead,
  tasksV1CardsDetailRetrieve,
} from '../fetch-client';
import { taskDetailsMapper } from '../mappers/task-details';
import { useFetchOpts } from '../use-fetch-opts';

export namespace GetTask {
  export const key = (input: Input) => [
    'task-manager',
    'task-details',
    input.id,
  ];

  export type QueryParams = {
    refetchInterval?: number;
  };

  export type Input = { id: string };
  export type Output = { task: TaskDetails };

  function bodyFromInput(
    input: Input,
    includePcsAlerts: boolean,
    customerType: CustomerType,
  ): BodyOf<typeof tasksV1CardsDetailRetrieve> {
    return {
      uuid: input.id,
      includePcsAlerts,
      customerType,
    };
  }

  function outputFromBody(data: GetCardDetailViewOutputRead): Output {
    return {
      task: taskDetailsMapper(
        data.card,
        data.device,
        data.alert_infos,
        data.comments,
        data.score_reset,
        data.custom_fields,
        data.is_subscribed,
        data.workstream_project_details,
      ),
    };
  }

  export const optimistic = Optimistic.makeUpdate<Output>();

  export function useQuery(
    input: Input,
    { refetchInterval }: QueryParams = {},
  ) {
    const getOpts = useFetchOpts();
    const featureFlag = useFeatureFlag();
    const me = useMe();

    return rq.useQuery<Output>({
      refetchInterval,
      queryKey: key(input),
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;

        const isNetworkError =
          error instanceof TypeError &&
          (error.message === 'Failed to fetch' ||
            error.message.includes('fetch') ||
            error.message.includes('timeout'));

        return isNetworkError;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      async queryFn({ signal }) {
        const opts = await getOpts();
        const body = bodyFromInput(input, featureFlag('pcs'), me.config.type);

        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), 30_000);

        const combinedSignal = (() => {
          if (!signal) return timeoutController.signal;

          const controller = new AbortController();
          const abort = () => controller.abort();

          signal.addEventListener('abort', abort);
          timeoutController.signal.addEventListener('abort', abort);

          return controller.signal;
        })();

        try {
          const data = await ok(
            tasksV1CardsDetailRetrieve(body, {
              ...opts,
              signal: combinedSignal,
            }),
          );
          clearTimeout(timeoutId);
          return outputFromBody(data);
        } catch (error) {
          clearTimeout(timeoutId);

          if (
            error instanceof TypeError &&
            error.message === 'Failed to fetch'
          ) {
            throw new Error(
              `Failed to retrieve task details (ID: ${input.id}). ` +
                'This may be due to a slow or unstable network connection. Please try again.',
            );
          }

          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(
              `Request timed out while retrieving task details (ID: ${input.id}). ` +
                'This may be due to high network latency. Please try again.',
            );
          }

          throw error;
        }
      },
    });
  }
}
